import { XMLParser } from 'fast-xml-parser';
import { Mark, Node } from '@remirror/pm/model';
import { EditorState } from '@remirror/pm/state';
import { closeHistory } from '@remirror/pm/history';
import { EditorSchema, ProsemirrorNode, RemirrorManager } from '@remirror/core';
import { ActiveCueGroup, CueType } from '../StudioCues/Cue';
import isPauseToken from './utils/isPauseToken';
import { CuesApplied } from '../../services/EventTrackingService';
import { UnionedExtensions } from './extensions';

/**
 * Function to convert text into an array of ProseMirror nodes
 */
export const getNodesFromText = (
  schema: EditorSchema,
  text: string
): Node[] => {
  if (text.trim().length === 0) {
    return [];
  }

  // Split the input text into lines using a regular expression
  const lineSplitRegex = /(?:\r\n|\r|\n)/g;
  const lines = text.split(lineSplitRegex);

  const nodes: Node[] = [];

  lines.forEach(line => {
    // If the line is empty, add an empty paragraph node
    if (!line) {
      nodes.push(Node.fromJSON(schema, { type: 'paragraph' }));
    } else {
      // Otherwise, create a new paragraph node with the line text as content
      const content = schema.text(line);
      const paragraph = schema.nodes.paragraph.create({}, content);
      nodes.push(paragraph);
    }
  });

  return nodes;
};

const NEW_LINE = '\n';
/**
 * Function to convert ProsemirrorState into plain text
 */
export const getTextFromState = (state: EditorState): string => {
  let content = '';
  let isFirstParagraph = true;

  state.doc.forEach((node: Node) => {
    if (node.type.name === 'paragraph') {
      // Add a newline before each paragraph (except the first one)
      if (!isFirstParagraph) {
        content += NEW_LINE;
      } else {
        isFirstParagraph = false;
      }
      content += node.textContent;
    }
  });

  return content;
};

export const getTextArrayFromState = (state: EditorState): string[] => {
  const script: string[] = [];
  state.doc.descendants((node: ProsemirrorNode) => {
    if (node.isText) {
      script.push(node.textContent);
    }
  });
  return script;
};

export const performPhoneticReplacement = (
  state: EditorState,
  useXml: boolean = false
): string => {
  let result: string = '';
  state.doc.forEach((node: ProsemirrorNode, index: number) => {
    if (node.type.name === 'paragraph') {
      node.forEach((subNode: ProsemirrorNode) => {
        const phoneticReplacementMark = isPhoneticReplacement(subNode);
        if (phoneticReplacementMark) {
          result += getFormattedTextFromPhoneticReplacement(subNode, useXml);
        } else {
          result += subNode.textContent;
        }
      });
      if (index !== state.doc.childCount - 1) {
        result += NEW_LINE;
      }
    }
  });
  return result;
};

export const isPhoneticReplacement = (
  node: ProsemirrorNode
): Mark | undefined => {
  if (
    node.type.name !== 'text' ||
    !Array.isArray(node.marks) ||
    node.marks.length < 1
  ) {
    return undefined;
  }
  return node.marks.find(m => m.type.name === 'phonetic') as Mark;
};

/**
 * XML is generated for the entire editor document
 */
export const getXmlFromState = (state: EditorState) => {
  let xml: string = '';
  state.doc.forEach((node: ProsemirrorNode, index: number) => {
    if (node.type.name === 'paragraph') {
      node.forEach(subNode => {
        if (subNode.type.name === 'text') {
          const respellXml = getFormattedTextFromPhoneticReplacement(
            subNode,
            true
          );
          if (respellXml && respellXml !== '') {
            xml = `${xml}${respellXml}`;
          } else {
            xml = `${xml}${subNode.textContent}`;
          }
        } else if (subNode.type.name === 'cue') {
          const cueXml = getXmlFromCue(subNode);
          xml = `${xml}${cueXml}`;
        }
      });
      if (index !== state.doc.childCount - 1) {
        xml = `${xml}${NEW_LINE}`;
      }
    }
  });
  return xml;
};

// phonetic replacement mark structure:
// phonetic({ replacement: 'world' })('hello')
export const getFormattedTextFromPhoneticReplacement = (
  node: ProsemirrorNode,
  useXml = false
): string => {
  let result = '';
  const phoneticMark = isPhoneticReplacement(node);
  if (phoneticMark) {
    if (phoneticMark.attrs.enabled) {
      let replacement = phoneticMark.attrs?.replacement as string;
      if (useXml) {
        // V11 logic
        if (phoneticMark.attrs?.isRespelling) {
          result = `<respell value="${replacement}">${node.textContent}</respell>`;
        } else {
          result = `${replacement}`;
        }
      } else {
        // V10 logic
        if (phoneticMark.attrs?.isRespelling) {
          replacement = `::${replacement}::`;
        }
        result = `${replacement}`;
      }
    } else {
      // replacement disabled, return original word
      result = `${node.textContent}`;
    }
  }
  return result;
};

// cue node structure:
// cue({ loudness: -100, tempo: 100 })(`It's raining tacos`)
export const getXmlFromCue = (cueNode: ProsemirrorNode): string => {
  let xml = '';
  if (cueNode.type.name !== 'cue') {
    return xml;
  }
  const { attrs } = cueNode;
  const pauseRaw: string | null =
    attrs.pause !== attrs.initialValue ? attrs.pause : null;

  // Exit early if the cue node uses the pause attribute since it's likely
  // to only be punctuation and not contain any phonetic replacement marks.
  if (pauseRaw) {
    const { loudness, tempo } = calculateLoudnessAndTempoFromPause(
      parseFloat(pauseRaw)
    );
    const { textContent } = cueNode;
    return `<tempo value="${tempo}"><loudness value="${loudness}">${textContent}</loudness></tempo>`;
  }

  const pitchRaw: string | null =
    attrs.pitch !== attrs.initialValue ? attrs.pitch : null;
  const loudnessRaw: string | null =
    attrs.loudness !== attrs.initialValue ? attrs.loudness : null;
  const tempoRaw: string | null =
    attrs.tempo !== attrs.initialValue ? attrs.tempo : null;

  // Note: A cue node could contain phonetic replacement marks. Need to process each sub node.
  cueNode.forEach((subNode: ProsemirrorNode) => {
    const subNodeXml = getFormattedTextFromPhoneticReplacement(subNode, true);
    if (subNodeXml && subNodeXml !== '') {
      xml = `${xml}${subNodeXml}`;
    } else {
      xml = `${xml}${subNode.textContent}`;
    }
  });

  if (pitchRaw) {
    const pitch = calculatePitch(parseFloat(pitchRaw));
    xml = `<pitch value="${pitch}">${xml}</pitch>`;
  }
  if (loudnessRaw) {
    const loudness = calculateLoudness(parseFloat(loudnessRaw));
    xml = `<loudness value="${loudness}">${xml}</loudness>`;
  }
  if (tempoRaw) {
    const tempo = calculateTempo(parseFloat(tempoRaw));
    xml = `<tempo value="${tempo}">${xml}</tempo>`;
  }

  return xml;
};

export const hasCues = (state: EditorState): boolean => {
  let hasCue = false;
  state.doc.descendants((node: ProsemirrorNode) => {
    if (hasCue) {
      return false;
    }
    if (node.type.name === 'cue') {
      /**
       * NOTE: This is handling for if a user edits a cue.
       * Currently if a cue exists and a user begins editing the cues text, a user can
       * delete all the text but still be left with an "empty" cue that has no text content
       * but remains in the remirror document. Until we address editing cues, this logic
       * is to address the fact that a cue is no longer visible to the user.
       */
      const isEmpty = node.textContent.length === 0;
      if (!isEmpty) {
        hasCue = true;
      }
    }
  });
  return hasCue;
};

export const findCues = (state: EditorState): CuesApplied | undefined => {
  let cues: CuesApplied | undefined;

  state.doc.descendants((node: ProsemirrorNode) => {
    if (node.type.name === 'cue') {
      if (!cues) {
        cues = {
          loudness: false,
          pace: false,
          pause: false,
          pitch: false,
        };
      }
      if (cues.loudness && cues.pace && cues.pause && cues.pitch) return;

      /**
       * NOTE: This is handling for if a user edits a cue.
       * Currently if a cue exists and a user begins editing the cues text, a user can
       * delete all the text but still be left with an "empty" cue that has no text content
       * but remains in the remirror document. Until we address editing cues, this logic
       * is to address the fact that a cue is no longer visible to the user.
       */
      const isEmpty = node.textContent.length === 0;
      if (!isEmpty) {
        const loudness = node.attrs[CueType.loudness];
        const tempo = node.attrs[CueType.tempo];
        const pause = node.attrs[CueType.pause];
        const pitch = node.attrs[CueType.pitch];

        if (loudness) cues.loudness = true;
        if (tempo) cues.pace = true;
        if (pause) cues.pause = true;
        if (pitch) cues.pitch = true;
      }
    }
  });
  return cues;
};

interface SliderValueConverter {
  [key: string]: string;
}

// Loudness range: -15 to 9 (quiet to loud)
// Note: From raw loudness input, larger value is louder
// Raw loudness 100 => 9
// Raw loudness -100 => -15
// Raw loudness 0 => 0
const loudnessRawConverter: SliderValueConverter = {
  '-100': '-15',
  '-80': '-12',
  '-60': '-8',
  '-40': '-4',
  '-20': '-2',
  '0': '0', // default
  '20': '2',
  '40': '4',
  '60': '6',
  '80': '8',
  '100': '9',
};
const calculateLoudness = (loudnessRaw: number): string => {
  return loudnessRawConverter[loudnessRaw.toString()] ?? '0';
};

// Tempo range: 0.7 to 2.3 (slow to fast)
// Note: From raw tempo input, larger value is faster
// Raw tempo 100 => 2.3
// Raw tempo -100 => 0.7
// Raw tempo 0 => 1
const tempoRawConverter: SliderValueConverter = {
  '-100': '0.7',
  '-80': '0.75',
  '-60': '0.8',
  '-40': '0.85',
  '-20': '0.9',
  '0': '1.0', // default
  '20': '1.3',
  '40': '1.6',
  '60': '1.9',
  '80': '2.2',
  '100': '2.3',
};
const calculateTempo = (tempoRaw: number): string => {
  return tempoRawConverter[tempoRaw.toString()] ?? '1.0';
};

// Pitch range: -45 to 100 (lowest to highest)
// Note: From raw pitch input, larger value is higher pitch
// Raw pitch 100 => 100
// Raw pitch -100 => -45
// Raw pitch 0 => 0
const pitchRawConverter: SliderValueConverter = {
  '-100': '-45',
  '-80': '-36',
  '-60': '-27',
  '-40': '-18',
  '-20': '-9',
  '0': '0',
  '20': '20',
  '40': '40',
  '60': '60',
  '80': '80',
  '100': '100',
};
const calculatePitch = (pitchRaw: number): string => {
  return pitchRawConverter[pitchRaw.toString()] ?? '0';
};

// Pauses are a combination of loudness and tempo
// Note: From raw pause input, larger value is longer, which is slower tempo (lower tempo value)
const pauseTempoRawConverter: SliderValueConverter = {
  '-100': '2.3',
  '-80': '2.1',
  '-60': '1.9',
  '-40': '1.6',
  '-20': '1.3',
  '0': '1.0', // default
  '20': '0.9',
  '40': '0.85',
  '60': '0.8',
  '80': '0.75',
  '100': '0.7',
};

// [25/12/02] Loudness for pause is static at -15
const pauseLoundnessRaw = '-15';

const calculateLoudnessAndTempoFromPause = (
  pauseRaw: number
): { tempo: string; loudness: string } => {
  const tempo = pauseTempoRawConverter[pauseRaw.toString()] ?? '1.00';
  const loudness = `${pauseLoundnessRaw}`;

  return {
    tempo,
    loudness,
  };
};

/**
 * Inverse to calculateLoudnessAndTempoFromPause
 */
const calculatePauseFromLoudnessAndTempo = (
  tempo: number | null,
  loudness: number | null
): string => {
  let xmlTempo = 1;
  if (tempo) {
    xmlTempo = tempo;
  }
  const tempoConverter = transformObject(pauseTempoRawConverter);
  // use the updated pause tempo converter to calculate raw pause value
  return tempoConverter[xmlTempo.toString()] ?? '0';
};

export const createEditorNodesFromXml = (
  xml: string,
  editorState: EditorState
): ProsemirrorNode[] => {
  const xmlRegex =
    /(?:<([a-zA-Z0-9_-]+)[^>]*>([\s\S]*?)<\/\1>)|([^<\n]+)|([<\n])/gs;
  const nodes: Array<ProsemirrorNode> = [];
  const xmlMatches = xml.matchAll(xmlRegex);
  const options = {
    ignoreAttributes: false,
    attributeNamePrefix: 'A_',
    alwaysCreateTextNode: true,
    preserveOrder: true,
    trimValues: false,
  };
  const parser = new XMLParser(options);
  let pContent: Array<string | ProsemirrorNode> = [];

  for (const [text, xmlTag] of xmlMatches) {
    if (text === NEW_LINE) {
      nodes.push(createParagraphNodeFromContents(pContent, editorState));
      pContent = [];
    } else if (xmlTag) {
      if (xmlTag === 'loudness' || xmlTag === 'tempo' || xmlTag === 'pitch') {
        const cueNode = createCueNodeFromXml(text, editorState, parser);
        if (cueNode) pContent.push(cueNode);
      } else if (xmlTag === 'respell') {
        const replacementNode = createReplacementMarkFromXml(
          text,
          editorState,
          parser
        );
        if (replacementNode) pContent.push(replacementNode);
      }
    } else {
      pContent.push(text);
    }
  }
  // add the last paragraph without linebreak
  if (pContent.length > 0) {
    nodes.push(createParagraphNodeFromContents(pContent, editorState));
  }
  return nodes;
};

interface NodeAttributes {
  [key: string]: string | number;
}

/**
 * Recursively search for a cue in the xml object
 */
const findCueInXmlObject = (xmlObj: any, cue: CueType): any | null => {
  // Check if the cue exists directly in the current object
  if (cue in xmlObj) {
    return xmlObj;
  }

  // If the key is not found directly, check for other cues in the nested object
  for (const prop of Object.keys(xmlObj)) {
    if (
      typeof xmlObj[prop] === 'object' &&
      Object.keys(CueType).includes(prop)
    ) {
      // recurse into the nested object and return if the key is found
      const result = findCueInXmlObject(xmlObj[prop][0], cue);
      if (result !== null) {
        return result;
      }
    }
  }

  return null;
};

// example xml: <tempo value="0.13"><loudness value="17.50">It's raining tacos.</loudness></tempo>
// Note: tempo would always be outside of loudness (from getXmlFromCue)
export const createCueNodeFromXml = (
  xml: string,
  editorState: EditorState,
  parser: XMLParser
): ProsemirrorNode | null => {
  const { schema } = editorState;
  const xmlObject = parser.parse(xml);

  if (!xmlObject || xmlObject.length === 0) {
    return null;
  }

  // Find the given cue inside the xml object
  const tempoObject = findCueInXmlObject(xmlObject[0], CueType.tempo);
  const loudnessObject = findCueInXmlObject(xmlObject[0], CueType.loudness);
  const pitchObject = findCueInXmlObject(xmlObject[0], CueType.pitch);

  if (tempoObject || loudnessObject || pitchObject) {
    // We need to use the xmlCueObj that contains text nodes or respellings, and that xmlCueObj will be
    // the node that does not contain another cue
    const xmlCueObjWithTextNodes = [
      pitchObject?.pitch,
      loudnessObject?.loudness,
      tempoObject?.tempo,
    ].find(
      xmlCueObj =>
        typeof xmlCueObj?.[0] === 'object' &&
        Object.keys(xmlCueObj[0]).every(
          key => !Object.keys(CueType).includes(key)
        )
    );

    const cueContents = getCueContents(xmlCueObjWithTextNodes, editorState);

    if (cueContents.length === 0) {
      return null;
    }

    const attrs: NodeAttributes = {};

    const isPause = cueContents.every(node => isPauseToken(node.textContent));
    if (isPause) {
      attrs.cueGroup = ActiveCueGroup.pause;

      const tempoValue = tempoObject[':@']?.A_value ?? null;
      const loudnessValue = loudnessObject[':@']?.A_value ?? null;
      attrs.pause = calculatePauseFromLoudnessAndTempo(
        tempoValue,
        loudnessValue
      );
    } else {
      attrs.cueGroup = ActiveCueGroup.word;

      if (tempoObject) {
        const tempoValue = tempoObject[':@']?.A_value ?? null;
        attrs.tempo = calculateTempoRaw(parseFloat(tempoValue));
      }

      if (loudnessObject) {
        const loudnessValue = loudnessObject[':@']?.A_value ?? null;
        attrs.loudness = calculateLoudnessRaw(parseFloat(loudnessValue));
      }

      if (pitchObject) {
        const pitchValue = pitchObject[':@']?.A_value ?? null;
        attrs.pitch = calculatePitchRaw(parseFloat(pitchValue));
      }
    }

    const cueNode = schema.nodes.cue.create(attrs, cueContents);
    return cueNode;
  }

  return null;
};

const getCueContents = (nodeObject: any, editorState: EditorState): Node[] => {
  const textNodes: Node[] = [];
  if (nodeObject && nodeObject.length > 0) {
    const { schema } = editorState;
    nodeObject.forEach((c: { [x: string]: string; respell: any }) => {
      if (c.respell) {
        const respellNode = createReplacementMarkFromObject(c, editorState);
        if (respellNode) {
          textNodes.push(respellNode);
        }
      } else if (c['#text']) {
        textNodes.push(schema.text(c['#text']));
      }
    });
  }
  return textNodes;
};

const transformObject = (obj: {
  [key: string]: string;
}): { [key: string]: string } => {
  const initObj: SliderValueConverter = {};
  return Object.entries(obj).reduce((transformedObj, [key, value]) => {
    // eslint-disable-next-line no-param-reassign
    transformedObj[value] = key;
    return transformedObj;
  }, initObj);
};

// reverse of calculateLoudness
const calculateLoudnessRaw = (loudness: number): string => {
  const loudnessConverter = transformObject(loudnessRawConverter);
  return loudnessConverter[loudness.toString()] ?? '0';
};

// reverse of calculateTempo
const calculateTempoRaw = (tempo: number): string => {
  const tempoConverter = transformObject(tempoRawConverter);
  return tempoConverter[tempo.toString()] ?? '0';
};

// reverse of calculatePitch
const calculatePitchRaw = (pitch: number): string => {
  const pitchConverter = transformObject(pitchRawConverter);
  return pitchConverter[pitch.toString()] ?? '0';
};

// example xml: <respell value="HEH-LOH">hello</respell>
export const createReplacementMarkFromXml = (
  xml: string,
  editorState: EditorState,
  parser: XMLParser
): ProsemirrorNode | null => {
  const xmlObject = parser.parse(xml);
  const nodeObject = xmlObject[0] ?? null;
  return createReplacementMarkFromObject(nodeObject, editorState);
};

const createReplacementMarkFromObject = (
  nodeObject: any,
  editorState: EditorState
): ProsemirrorNode | null => {
  let respellNode: ProsemirrorNode;
  if (nodeObject) {
    const { schema } = editorState;
    const replacement = (nodeObject[':@']?.A_value as string) ?? null;
    const textContent = (nodeObject.respell[0]['#text'] as string) ?? null;
    if (textContent) {
      if (replacement) {
        // if replacment value exist, create a text node with phonetic mark
        const attrs: NodeAttributes = { replacement };
        respellNode = schema.text(textContent, [
          schema.marks.phonetic.create(attrs),
        ]);
      } else {
        // otherwise create a plain text node
        respellNode = schema.text(textContent);
      }
      return respellNode;
    }
  }
  return null;
};

const createParagraphNodeFromContents = (
  contents: Array<string | ProsemirrorNode>,
  editorState: EditorState
): ProsemirrorNode => {
  const { schema } = editorState;
  const nodes: ProsemirrorNode[] = contents.map(c => {
    if (typeof c === 'string') {
      return schema.text(c);
    }
    return c;
  });
  return schema.nodes.paragraph.create(null, nodes);
};

/**
 * Our editor comes with a history extension by default. Because this
 * extension doesn't have a clear history function of any kind,
 * the easiest way to clear out the history is to
 * - create a new state based on the existing doc
 * - applying closeHistory transaction, and returns the new state
 */
export const resetEditorHistory = (
  manager: RemirrorManager<UnionedExtensions>
): EditorState => {
  const { tr, doc } = manager.view.state;
  const state = manager.createState({ content: doc });
  const { state: newState } = state.applyTransaction(closeHistory(tr));
  return newState;
};

export const countActiveReplacements = (editorState: EditorState): number => {
  let count = 0;
  editorState.doc.forEach((node: ProsemirrorNode, index: number) => {
    if (node.type.name === 'paragraph') {
      node.forEach(subNode => {
        if (subNode.type.name === 'text') {
          const mark = isPhoneticReplacement(subNode);
          if (mark && mark.attrs.enabled) {
            count += 1;
          }
        } else if (subNode.type.name === 'cue') {
          subNode.forEach((cueSubNode: ProsemirrorNode) => {
            const mark = isPhoneticReplacement(cueSubNode);
            if (mark && mark.attrs.enabled) {
              count += 1;
            }
          });
        }
      });
    }
  });
  return count;
};

export default {
  getNodesFromText,
  getTextFromState,
  getTextArrayFromState,
  getXmlFromState,
  createEditorNodesFromXml,
  resetEditorHistory,
  hasCues,
  performPhoneticReplacement,
};
